import { observer } from 'mobx-react-lite'
import { FormGroupContainer } from '@/components/FormContainer'
import { NumericInput } from '@/components/inputs/NumericInput'
import type { MutatorEditorViewModelBase } from '@/features/legends/effects/MutatorEditorViewModelBase'
import type { AnyMutatorEditor } from '@/features/legends/effects/mutator-registry'
import { MutatorAmount } from '@/features/legends/effects/components/MutatorAmount'
import { MutatorForm } from '@/features/legends/effects/components/MutatorForm'
import { Sentence } from '@/features/legends/components/Sentence'
import type { CopyInventoryItemMutatorEditorViewModel } from '@/features/legends/effects/mutators/CopyInventoryItemMutatorEditorViewModel'
import type {
  InventoryItemOperator,
  ListOperator,
  VaryOperator,
} from '@/features/legends/effects/effects'
import type {
  InventoryItemPropertiesChoice,
  InventoryItemMutatorEditorViewModel,
} from '@/features/legends/effects/mutators/InventoryItemMutatorEditorViewModel'
import type { ListMutatorEditorViewModel } from '@/features/legends/effects/mutators/ListMutatorEditorViewModel'
import type { VaryMutatorEditorViewModel } from '@/features/legends/effects/mutators/VaryMutatorEditorViewModel'
import {
  type Choice,
  ChoiceSelect,
} from '@/features/legends/components/ChoiceSelect'
import { FormulaFieldContainer } from '@/features/legends/ryno/components/FormulaFieldContainer'
import { RynoEditorContainer } from '@/features/legends/ryno/components/RynoEditor/RynoEditorContainer'
import { SymbolConnectorContainer } from '@/features/legends/symbols/components/SymbolConnector/SymbolConnectorContainer'
import { SymbolConnectorListContainer } from '@/features/legends/symbols/components/SymbolConnectorList/SymbolConnectorListContainer'

export interface MutatorEditorContainerProps {
  vm: AnyMutatorEditor
  /**
   * Whether service item mutators set an amount; see
   * `EffectEditorViewModel.hasServiceItemAmount`.
   */
  hasServiceItemAmount: boolean
  testId?: string
}

/**
 * The editor for a mutator of any type.
 */
export const MutatorEditorContainer = observer(function MutatorEditorContainer({
  vm,
  hasServiceItemAmount,
  testId,
}: MutatorEditorContainerProps) {
  switch (vm.type) {
    case 'InventoryItem':
      return (
        <InventoryItemMutatorContainer
          vm={vm}
          hasAmount={hasServiceItemAmount}
          testId={testId}
        />
      )
    case 'CopyInventoryItem':
      return <CopyInventoryItemMutatorContainer vm={vm} testId={testId} />
    case 'ClearInventoryItemQuantity':
      return (
        <MutatorForm>
          <TargetsContainer vm={vm} testId={testId && `${testId}-targets`} />
        </MutatorForm>
      )
    case 'Vary':
      return <VaryMutatorContainer vm={vm} testId={testId} />
    case 'List':
      return <ListMutatorContainer vm={vm} testId={testId} />
    case 'Replace':
      return (
        <MutatorForm>
          <TargetsContainer vm={vm} testId={testId && `${testId}-targets`} />
          <FormulaFieldContainer
            vm={vm.expr}
            error={vm.validation.getError('expr')}
            isDisabled={vm.isEditingDisabled}
            aria-label="Value formula"
            testId={testId && `${testId}-formula`}
          />
        </MutatorForm>
      )
  }
})

interface TargetsContainerProps {
  vm: MutatorEditorViewModelBase
  label?: string
  placeholder?: string
  testId?: string
}

const TargetsContainer = observer(function TargetsContainer({
  vm,
  label,
  placeholder,
  testId,
}: TargetsContainerProps) {
  return (
    <FormGroupContainer
      label={label}
      // The connectors show their own errors once there are any.
      error={
        vm.targets.count === 0 ? vm.validation.getError('targets') : undefined
      }
    >
      <SymbolConnectorListContainer
        vm={vm.targets}
        isDisabled={vm.isEditingDisabled}
        placeholder={placeholder}
        testId={testId}
      />
    </FormGroupContainer>
  )
})

const inventoryItemOps: Choice<InventoryItemOperator>[] = [
  ['Inc', 'Increment'],
  ['Dec', 'Decrement'],
  ['Replace', 'Replace'],
  ['Mult', 'Multiply'],
]

const inventoryItemProperties: Choice<InventoryItemPropertiesChoice>[] = [
  ['Quantity', 'quantity'],
  ['CourtesyMultiplier', 'courtesy multiplier'],
  ['Both', 'quantity + courtesy multiplier'],
]

interface InventoryItemMutatorContainerProps {
  vm: InventoryItemMutatorEditorViewModel
  hasAmount: boolean
  testId?: string
}

const InventoryItemMutatorContainer = observer(
  function InventoryItemMutatorContainer({
    vm,
    hasAmount,
    testId,
  }: InventoryItemMutatorContainerProps) {
    return (
      <MutatorForm>
        <Sentence>
          <ChoiceSelect
            value={vm.op}
            choices={inventoryItemOps}
            onChange={vm.setOperator}
            isDisabled={vm.isEditingDisabled}
            size="small"
            aria-label="Operation"
            testId={testId && `${testId}-operation`}
          />
          <ChoiceSelect
            value={vm.propertiesChoice}
            choices={inventoryItemProperties}
            onChange={vm.setPropertiesChoice}
            isDisabled={vm.isEditingDisabled}
            size="small"
            aria-label="Properties"
            testId={testId && `${testId}-properties`}
          />
        </Sentence>
        <TargetsContainer vm={vm} testId={testId && `${testId}-targets`} />
        {hasAmount && (
          <MutatorAmount
            isExprMode={vm.isExprMode}
            onToggleMode={vm.toggleMode}
            error={vm.isExprMode ? vm.validation.getError('expr') : undefined}
            isDisabled={vm.isEditingDisabled}
            testId={testId && `${testId}-amount`}
          >
            {vm.isExprMode ? (
              <RynoEditorContainer
                vm={vm.expr}
                isDisabled={vm.isEditingDisabled}
                isCompact
                aria-label="Amount formula"
                testId={testId && `${testId}-amount-formula`}
              />
            ) : (
              <NumericInput
                value={vm.quantity}
                onChange={vm.setQuantity}
                mode="draft"
                precision={10}
                fixedPrecision={false}
                allowNegative
                isDisabled={vm.isEditingDisabled}
                placeholder="Amount"
                aria-label="Amount"
                testId={testId && `${testId}-amount-input`}
              />
            )}
          </MutatorAmount>
        )}
      </MutatorForm>
    )
  },
)

interface CopyInventoryItemMutatorContainerProps {
  vm: CopyInventoryItemMutatorEditorViewModel
  testId?: string
}

const CopyInventoryItemMutatorContainer = observer(
  function CopyInventoryItemMutatorContainer({
    vm,
    testId,
  }: CopyInventoryItemMutatorContainerProps) {
    return (
      <MutatorForm>
        <FormGroupContainer
          label="Copy"
          error={vm.validation.getError('source')}
        >
          <SymbolConnectorContainer
            vm={vm.source}
            isDisabled={vm.isEditingDisabled}
            placeholder="Source item to copy from"
            testId={testId && `${testId}-source`}
          />
        </FormGroupContainer>
        <TargetsContainer
          vm={vm}
          label="to"
          placeholder="Target item to copy to"
          testId={testId && `${testId}-targets`}
        />
      </MutatorForm>
    )
  },
)

const varyOps: Choice<VaryOperator>[] = [
  ['Inc', 'Increment'],
  ['Dec', 'Decrement'],
]

interface VaryMutatorContainerProps {
  vm: VaryMutatorEditorViewModel
  testId?: string
}

const VaryMutatorContainer = observer(function VaryMutatorContainer({
  vm,
  testId,
}: VaryMutatorContainerProps) {
  return (
    <MutatorForm>
      <Sentence>
        <ChoiceSelect
          value={vm.op}
          choices={varyOps}
          onChange={vm.setOperator}
          isDisabled={vm.isEditingDisabled}
          size="small"
          aria-label="Operation"
          testId={testId && `${testId}-operation`}
        />
      </Sentence>
      <TargetsContainer vm={vm} testId={testId && `${testId}-targets`} />
      <FormulaFieldContainer
        vm={vm.expr}
        error={vm.validation.getError('expr')}
        isDisabled={vm.isEditingDisabled}
        aria-label="Value formula"
        testId={testId && `${testId}-formula`}
      />
    </MutatorForm>
  )
})

const listOps: Choice<ListOperator>[] = [
  ['Add', 'Add'],
  ['Remove', 'Remove'],
]

const addCounts: Choice<boolean>[] = [
  [false, 'one value to'],
  [true, 'multiple values to'],
]

const removeCounts: Choice<boolean>[] = [
  [false, 'one value from'],
  [true, 'multiple values from'],
]

interface ListMutatorContainerProps {
  vm: ListMutatorEditorViewModel
  testId?: string
}

const ListMutatorContainer = observer(function ListMutatorContainer({
  vm,
  testId,
}: ListMutatorContainerProps) {
  return (
    <MutatorForm>
      <Sentence>
        <ChoiceSelect
          value={vm.op}
          choices={listOps}
          onChange={vm.setOperator}
          isDisabled={vm.isEditingDisabled}
          size="small"
          aria-label="Operation"
          testId={testId && `${testId}-operation`}
        />
        <ChoiceSelect
          value={vm.multi}
          choices={vm.op === 'Add' ? addCounts : removeCounts}
          onChange={vm.setMulti}
          isDisabled={vm.isEditingDisabled}
          size="small"
          aria-label="How many values"
          testId={testId && `${testId}-count`}
        />
      </Sentence>
      <TargetsContainer vm={vm} testId={testId && `${testId}-targets`} />
      <FormulaFieldContainer
        vm={vm.expr}
        error={vm.validation.getError('expr')}
        isDisabled={vm.isEditingDisabled}
        aria-label="Value formula"
        testId={testId && `${testId}-formula`}
      />
    </MutatorForm>
  )
})
